import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { SUPPORT_EMAIL, ENTITY_NAME } from '../../legal';
import SiteDisclaimer from './SiteDisclaimer';

/**
 * Footer rendered at the bottom of Consumer / Retailer / Admin shells.
 *
 * Footer link composition is per-app — see the launch-list table:
 *   consumer : Terms, Privacy, Cookies, Refund, Support
 *   retailer : Retailer Agreement, Refund, Privacy, Support
 *   admin    : Terms, Privacy, Support
 *
 * Pilot agreements (Consumer Pilot, Retailer Pilot) are intentionally NOT
 * exposed here — they're only shown via the in-app acceptance modal during
 * onboarding (built in Phase 2).
 */
const LINK_MAP = {
  consumer: [
    { to: '/terms', label: 'Terms of Service' },
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/cookies', label: 'Cookie Policy' },
    { to: '/refund-policy', label: 'Refund & Recall Policy' },
  ],
  retailer: [
    { to: '/retailer-agreement', label: 'Retailer Agreement' },
    { to: '/refund-policy', label: 'Refund & Recall Policy' },
    { to: '/privacy', label: 'Privacy Policy' },
  ],
  admin: [
    { to: '/terms', label: 'Terms of Service' },
    { to: '/privacy', label: 'Privacy Policy' },
  ],
};

export default function Footer({ variant = 'consumer' }) {
  const links = LINK_MAP[variant] || LINK_MAP.consumer;
  const year = new Date().getFullYear();

  return (
    <footer
      className="border-t border-gray-200 bg-white mt-auto"
      data-testid={`footer-${variant}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="pb-4 mb-4 border-b border-gray-100">
          <SiteDisclaimer />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-gray-600 hover:text-emerald-700 transition-colors"
                data-testid={`footer-link-${l.to.replace(/\//g, '-').replace(/^-/, '')}`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-1.5 text-gray-600 hover:text-emerald-700 transition-colors"
              data-testid="footer-support-link"
            >
              <Mail className="w-3.5 h-3.5" /> {SUPPORT_EMAIL}
            </a>
          </nav>
          <p className="text-xs text-gray-400">
            © {year} {ENTITY_NAME}
          </p>
        </div>
      </div>
    </footer>
  );
}
